import { denormalize } from 'normalizr';
import { createSelector } from 'reselect';
import { stakeholderParticipation as stakeholderParticipationSchema } from '../lib/schema.js';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { selectEntities } from './entities.js';

// API Actions

const RETRIEVE_PARTICIPATIONS_REQUEST =
  'changesync/stakeholderParticipations/RETRIEVE_PARTICIPATIONS_REQUEST';
const RETRIEVE_CHANGE_IMPACT_PARTICIPATIONS_SUCCESS =
  'changesync/stakeholderParticipations/RETRIEVE_CHANGE_IMPACT_PARTICIPATIONS_SUCCESS';
const RETRIEVE_INITIATIVE_PARTICIPATIONS_SUCCESS =
  'changesync/stakeholderParticipations/RETRIEVE_INITIATIVE_PARTICIPATIONS_SUCCESS';
const RETRIEVE_PARTICIPATIONS_FAILURE =
  'changesync/stakeholderParticipations/RETRIEVE_PARTICIPATIONS_FAILURE';
const UPDATE_PARTICIPATION_REQUEST =
  'changesync/stakeholderParticipations/UPDATE_PARTICIPATION_REQUEST';
const UPDATE_PARTICIPATION_SUCCESS =
  'changesync/stakeholderParticipations/UPDATE_PARTICIPATION_SUCCESS';
const UPDATE_PARTICIPATION_FAILURE =
  'changesync/stakeholderParticipations/UPDATE_PARTICIPATION_FAILURE';

const UPDATE_SEARCH_QUERY =
  'changesync/stakeholderParticipations/UPDATE_SEARCH_QUERY';
const SEARCH_PARTICIPATIONS =
  'changesync/stakeholderParticipations/SEARCH_PARTICIPATIONS';
// Initial State

const initialState = {
  /**
   * @example
   * changeImpactParticipationIds {
   *   [changeImpactId]: [participationId, participationId, ...]
   * }
   * initiativeParticipationIds {
   *
   *   [initiativeType+initiativeId]: [participationId, participationId, ...]
   * }
   */

  changeImpactParticipationIds: [],
  initiativeParticipationIds: [],
  pending: false,
  success: false,
  error: null,
};

// Reducer

export default (state = initialState, action) => {
  switch (action.type) {
    case RETRIEVE_PARTICIPATIONS_REQUEST:
      return {
        ...state,
        pending: true,
        success: false,
        error: null,
      };
    case RETRIEVE_CHANGE_IMPACT_PARTICIPATIONS_SUCCESS:
      return {
        ...state,
        changeImpactParticipationIds: action.payload.result,
        pending: false,
        success: true,
      };
    case RETRIEVE_INITIATIVE_PARTICIPATIONS_SUCCESS:
      return {
        ...state,
        initiativeParticipationIds: action.payload.result,
        pending: false,
        success: true,
      };
    case RETRIEVE_PARTICIPATIONS_FAILURE:
      return {
        ...state,
        pending: false,
        error: action.error,
      };

    case UPDATE_SEARCH_QUERY:
      return {
        ...state,
        query: action.payload,
      };
    default:
      return state;
  }
};

// Action Creators
export const retrieveChangeImpactStakeholderParticipations =
  (changeImpactId) => (dispatch) => {
    return dispatch({
      [CALL_API]: {
        types: [
          RETRIEVE_PARTICIPATIONS_REQUEST,
          RETRIEVE_CHANGE_IMPACT_PARTICIPATIONS_SUCCESS,
          RETRIEVE_PARTICIPATIONS_FAILURE,
        ],
        endpoint: `change_impacts/${changeImpactId}/stakeholder_participations`,
        method: HTTP_METHODS.GET,
        schema: [stakeholderParticipationSchema],
      },
    });
  };

export const searchChangeImpactStakeholderParticipations =
  (changeImpactId, query) => (dispatch) => {
    const searchParam = query ? `?search=${query}` : '';

    return dispatch({
      [CALL_API]: {
        types: [
          RETRIEVE_PARTICIPATIONS_REQUEST,
          RETRIEVE_CHANGE_IMPACT_PARTICIPATIONS_SUCCESS,
          RETRIEVE_PARTICIPATIONS_FAILURE,
        ],
        endpoint: `change_impacts/${changeImpactId}/stakeholder_participations${searchParam}`,
        method: HTTP_METHODS.GET,
        schema: [stakeholderParticipationSchema],
      },
      meta: {
        debounce: {
          time: 400,
          key: SEARCH_PARTICIPATIONS,
        },
      },
    });
  };

export const retrieveInitiativeStakeholderParticipations =
  (type, initiativeId) => (dispatch) => {
    return dispatch({
      [CALL_API]: {
        types: [
          RETRIEVE_PARTICIPATIONS_REQUEST,
          RETRIEVE_INITIATIVE_PARTICIPATIONS_SUCCESS,
          RETRIEVE_PARTICIPATIONS_FAILURE,
        ],
        endpoint: `${type}_initiatives/${initiativeId}/stakeholder_participations`,
        method: HTTP_METHODS.GET,
        schema: [stakeholderParticipationSchema],
      },
    });
  };

export const updateStakeholderParticipation = (
  id,
  stakeholderParticipation
) => ({
  [CALL_API]: {
    types: [
      UPDATE_PARTICIPATION_REQUEST,
      UPDATE_PARTICIPATION_SUCCESS,
      UPDATE_PARTICIPATION_FAILURE,
    ],
    endpoint: `stakeholder_participations/${id}`,
    method: HTTP_METHODS.PUT,
    body: {
      stakeholderParticipation,
    },
    schema: stakeholderParticipationSchema,
  },
});

export const searchInitiativeStakeholderParticipations =
  (type, initiativeId, query) => (dispatch) => {
    const searchParam = query ? `?search=${query}` : '';

    return dispatch({
      [CALL_API]: {
        types: [
          RETRIEVE_PARTICIPATIONS_REQUEST,
          RETRIEVE_INITIATIVE_PARTICIPATIONS_SUCCESS,
          RETRIEVE_PARTICIPATIONS_FAILURE,
        ],
        endpoint: `${type}_initiatives/${initiativeId}/stakeholder_participations${searchParam}`,
        method: HTTP_METHODS.GET,
        schema: [stakeholderParticipationSchema],
      },
      meta: {
        debounce: {
          time: 400,
          key: SEARCH_PARTICIPATIONS,
        },
      },
    });
  };

// Selectors
const selectChangeImpactStakeholdersIds = (state) =>
  state.stakeholderParticipations.changeImpactParticipationIds;

export const selectChangeImpactStakeholders = createSelector(
  selectChangeImpactStakeholdersIds,
  selectEntities,
  (changeImpactParticipationIds, entities) =>
    denormalize(
      changeImpactParticipationIds,
      [stakeholderParticipationSchema],
      entities
    )
);

const selectInitiativeStakeholdersIds = (state) =>
  state.stakeholderParticipations.initiativeParticipationIds;

export const selectInitiativeStakeholders = createSelector(
  selectInitiativeStakeholdersIds,
  selectEntities,
  (initiativeParticipationIds, entities) =>
    denormalize(
      initiativeParticipationIds,
      [stakeholderParticipationSchema],
      entities
    )
);
